/**
 * SSO Orchestrator client.
 *
 * The SSO Orchestrator is the WYA service that bridges Kratos with Polis
 * (the multi-tenant SAML/OIDC broker) and the customer's IdP (Entra ID,
 * Okta, etc.). For the dashboard, the only direct interaction is the
 * `/sso/discovery` endpoint: given an email, it tells us whether that
 * email's tenant has SSO enabled and emits a short-lived `loginToken`
 * that we forward to Kratos as `upstream_parameters.login_hint`. Kratos
 * passes it through Hydra to the orchestrator, which uses it to resolve
 * tenant + connection.
 *
 * See wya-platform/wya-platform-sso-orchestrator for the implementation.
 */

const SSO_DISCOVERY_URL = import.meta.env.VITE_SSO_DISCOVERY_URL as
  | string
  | undefined;

export interface SsoDiscoveryResult {
  ssoEnabled: boolean;
  /** Provider id registered in Kratos (`wya-sso`). Only present when ssoEnabled=true. */
  providerId?: string;
  /** Short-lived JWT (60s) carrying tenant_id + email_hash. Forwarded to Kratos as login_hint. */
  loginToken?: string;
  /**
   * V1 will populate this when the tenant has `sso_config.enforced=true`,
   * meaning the dashboard must NOT offer password/passkey/code methods
   * for that email — only SSO. Today the spike Orchestrator does not
   * emit it; default false here.
   */
  enforced?: boolean;
}

/**
 * Asks the SSO Orchestrator whether `email`'s tenant has SSO enabled.
 *
 * Returns `{ ssoEnabled: false }` if the env var is not configured (e.g.
 * local dev without SSO infra), so callers can keep working with the
 * regular Kratos flow.
 */
export async function discoverSso(email: string): Promise<SsoDiscoveryResult> {
  if (!SSO_DISCOVERY_URL) {
    console.warn(
      "VITE_SSO_DISCOVERY_URL not set; skipping SSO discovery (assuming disabled).",
    );
    return { ssoEnabled: false };
  }
  try {
    const res = await fetch(SSO_DISCOVERY_URL, {
      method: "POST",
      headers: { "content-type": "application/json" },
      // No credentials — this endpoint does not use cookies.
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      console.error(
        `SSO discovery returned ${res.status}; falling back to non-SSO flow.`,
      );
      return { ssoEnabled: false };
    }
    return (await res.json()) as SsoDiscoveryResult;
  } catch (err) {
    // Network errors must NOT block the password fallback. Log and pretend
    // SSO is disabled so the user can still log in via password/passkey/code.
    console.error("SSO discovery failed:", err);
    return { ssoEnabled: false };
  }
}

/**
 * Hands off the current Kratos login flow to the OIDC provider `wya-sso`
 * by submitting a top-level POST form to `flow.ui.action`. The browser
 * will navigate through:
 *
 *   Kratos → Hydra (/oauth2/auth) → SSO Orchestrator (/oauth2/login) →
 *   Polis (/api/oauth/authorize) → Customer IdP (Entra/Okta/...) →
 *   Polis callback → SSO Orchestrator (/sso/callback) → Hydra (consent) →
 *   Kratos OIDC callback → Dashboard (cookie set).
 *
 * The `loginToken` is sent as `upstream_parameters.login_hint`. That is
 * the only per-request whitelist field that Kratos forwards verbatim to
 * the OIDC provider's request_url; the SSO Orchestrator extracts it
 * there to resolve tenant + connection.
 */
export function submitOidcLoginForm(args: {
  flowAction: string;
  csrfToken: string;
  loginToken: string;
}): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = args.flowAction;
  form.style.display = "none";

  const fields: Record<string, string> = {
    csrf_token: args.csrfToken,
    method: "oidc",
    provider: "wya-sso",
    "upstream_parameters.login_hint": args.loginToken,
  };

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}
